<template>
  <!-- Panel presupuesto / valor ganado por grupos (por ahora solo para elecnor1 que usa budget de grupos) -->
  <div class="dashboard-subpanel-budget panel overflow-hidden">
    <!-- Header -->
    <div class="dashboard-subpanel-budget__hero">
      <div class="dashboard-subpanel-budget__hero-content">
        <div class="dashboard-subpanel-budget__hero-icon">
          <b-icon pack="fas" icon="chart-pie" size="is-medium" />
        </div>
        <div>
          <h3 class="dashboard-subpanel-budget__title">{{ $t('budget_overview') }}</h3>
          <p class="dashboard-subpanel-budget__subtitle">{{ $t('budget_overview_subtitle') }}</p>
        </div>
      </div>

      <nav v-if="breadcrumb.length > 1" class="dashboard-subpanel-budget__breadcrumb" aria-label="breadcrumb">
        <button
          v-for="(crumb, index) in breadcrumb"
          :key="'crumb_' + (crumb.id || 'root')"
          type="button"
          class="dashboard-subpanel-budget__breadcrumb-item"
          :class="{ 'dashboard-subpanel-budget__breadcrumb-item--active': index === breadcrumb.length - 1 }"
          :disabled="index === breadcrumb.length - 1 || isBusy"
          @click="navigateTo(index)"
        >
          <span>{{ crumb.name }}</span>
          <b-icon
            v-if="index < breadcrumb.length - 1"
            pack="fas"
            icon="chevron-right"
            size="is-small"
            class="dashboard-subpanel-budget__breadcrumb-sep"
          />
        </button>
      </nav>
    </div>

    <div
      class="dashboard-subpanel-budget__body"
      :class="{ 'dashboard-subpanel-budget__body--refreshing': isRefreshing }"
    >
      <div v-if="isInitialLoading" class="dashboard-subpanel-budget__loading">
        <scale-loader />
      </div>

      <template v-else>
      <!-- KPI strip -->
      <div class="dashboard-subpanel-budget__kpis">
        <div class="dashboard-subpanel-budget__kpi dashboard-subpanel-budget__kpi--budget">
          <div class="dashboard-subpanel-budget__kpi-label">{{ $t('budget') }}</div>
          <div class="dashboard-subpanel-budget__kpi-value">{{ formatCurrencyAbbrev(summary.totalBudget) }}</div>
          <div class="dashboard-subpanel-budget__kpi-hint">{{ summary.groupsWithBudget }} / {{ groups.length }} {{ $t('groups').toLowerCase() }}</div>
        </div>

        <div class="dashboard-subpanel-budget__kpi dashboard-subpanel-budget__kpi--progress">
          <div class="dashboard-subpanel-budget__kpi-label">{{ $t('weighted_average_progress') }}</div>
          <div class="dashboard-subpanel-budget__kpi-value">{{ Math.round(summary.weightedProgress) }}%</div>
          <div class="dashboard-subpanel-budget__kpi-ring" :style="progressRingStyle(summary.weightedProgress)">
            <span>{{ Math.round(summary.weightedProgress) }}%</span>
          </div>
        </div>

        <div class="dashboard-subpanel-budget__kpi dashboard-subpanel-budget__kpi--earned">
          <div class="dashboard-subpanel-budget__kpi-label">{{ $t('earned_value') }}</div>
          <div class="dashboard-subpanel-budget__kpi-value">{{ formatCurrencyAbbrev(summary.totalEarned) }}</div>
          <div class="dashboard-subpanel-budget__kpi-hint">
            {{ earnedRatioLabel }}
          </div>
        </div>

        <div class="dashboard-subpanel-budget__kpi dashboard-subpanel-budget__kpi--incurred">
          <div class="dashboard-subpanel-budget__kpi-label">{{ $t('incurred_cost') }}</div>
          <div class="dashboard-subpanel-budget__kpi-value">{{ formatCurrencyAbbrev(summary.totalIncurred) }}</div>
          <div class="dashboard-subpanel-budget__kpi-hint">
            {{ incurredRatioLabel }}
          </div>
        </div>
      </div>

      <div v-if="!groups.length" class="dashboard-subpanel-budget__empty">
        {{ $t('no_results') }}
      </div>

      <template v-else>
        <!-- Chart -->
        <div v-if="chartRows.length" class="dashboard-subpanel-budget__chart-wrap">
          <div class="dashboard-subpanel-budget__section-title">{{ $t('budget_vs_earned_value') }}</div>
          <column-chart
            :data="chartRows"
            :colors="['#6366f1', '#10b981', '#f59e0b']"
            :suffix="' ' + projectCurrency"
            :library="chartLibrary"
            height="280px"
          />
        </div>

        <!-- Group cards -->
        <div class="dashboard-subpanel-budget__section-title dashboard-subpanel-budget__section-title--cards">
          {{ $t('budget_by_group') }}
          <span class="dashboard-subpanel-budget__section-hint">{{ $t('budget_drilldown_hint') }}</span>
        </div>

        <div class="dashboard-subpanel-budget__grid">
          <button
            v-for="group in sortedGroups"
            :key="group.id"
            type="button"
            class="dashboard-subpanel-budget__card"
            :class="{
              'dashboard-subpanel-budget__card--clickable': canDrillDown(group),
              'dashboard-subpanel-budget__card--no-budget': groupBudgetRaw(group) === null,
            }"
            :style="cardAccentStyle(group)"
            :disabled="!canDrillDown(group) || isBusy"
            @click="drillDown(group)"
          >
            <div class="dashboard-subpanel-budget__card-header">
              <div
                class="dashboard-subpanel-budget__ring"
                :style="progressRingStyle(groupProgressCurrent(group), groupColor(group))"
              >
                <span>{{ Math.round(groupProgressCurrent(group)) }}%</span>
              </div>

              <div class="dashboard-subpanel-budget__card-titles">
                <div v-if="group.code" class="dashboard-subpanel-budget__card-code">{{ group.code }}</div>
                <div class="dashboard-subpanel-budget__card-name">{{ group.name }}</div>
              </div>

              <b-icon
                v-if="canDrillDown(group)"
                pack="fas"
                icon="chevron-right"
                class="dashboard-subpanel-budget__card-chevron"
              />
            </div>

            <div class="dashboard-subpanel-budget__card-metrics">
              <div class="dashboard-subpanel-budget__metric">
                <span class="dashboard-subpanel-budget__metric-label">{{ $t('progress') }}</span>
                <span class="dashboard-subpanel-budget__metric-value">{{ groupProgressDisplay(group) }}</span>
              </div>
              <div class="dashboard-subpanel-budget__metric">
                <span class="dashboard-subpanel-budget__metric-label">{{ $t('budget') }}</span>
                <span class="dashboard-subpanel-budget__metric-value">{{ groupBudgetDisplay(group) }}</span>
              </div>
              <div class="dashboard-subpanel-budget__metric">
                <span class="dashboard-subpanel-budget__metric-label">{{ $t('earned_value') }}</span>
                <span class="dashboard-subpanel-budget__metric-value dashboard-subpanel-budget__metric-value--earned">
                  {{ groupEarnedValueDisplay(group) }}
                </span>
              </div>
              <div class="dashboard-subpanel-budget__metric">
                <span class="dashboard-subpanel-budget__metric-label">{{ $t('incurred_cost') }}</span>
                <span class="dashboard-subpanel-budget__metric-value dashboard-subpanel-budget__metric-value--incurred">
                  {{ groupIncurredCostDisplay(group) }}
                </span>
              </div>
            </div>

            <!-- muestra una linea con el porcentage progreso grupo, redundante, la oculto por ahora
            <div class="dashboard-subpanel-budget__card-bar">
              <div
                class="dashboard-subpanel-budget__card-bar-fill"
                :style="{
                  width: groupProgressCurrent(group) + '%',
                  backgroundColor: groupColor(group),
                }"
              />
            </div> -->
          </button>
        </div>
      </template>
      </template>

      <div v-if="isRefreshing" class="dashboard-subpanel-budget__overlay" aria-live="polite" aria-busy="true">
        <scale-loader />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import { mapGetters } from 'vuex'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import VueChartkick from 'vue-chartkick'
import Chart from 'chart.js'

Vue.use(VueChartkick, { adapter: Chart })

const DEFAULT_GROUP_COLOR = '#6366f1'
const CHART_LABEL_MAX = 28

export default {
  name: 'dashboard-subpanel-budget',

  components: {
    ScaleLoader,
  },

  props: {
    projectId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isInitialLoading: false,
      isRefreshing: false,
      groups: [],
      breadcrumb: [],
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    projectCurrency() {
      return (this.project && this.project.currency) || '€'
    },

    chartLibrary() {
      const currency = this.projectCurrency
      return {
        legend: { display: true, position: 'bottom' },
        tooltips: {
          callbacks: {
            label(tooltipItem, data) {
              const label = data.datasets[tooltipItem.datasetIndex].label || ''
              const value = tooltipItem.yLabel
              if (value >= 1e6) return `${label}: ${(value / 1e6).toFixed(2)}M ${currency}`
              if (value >= 1e3) return `${label}: ${(value / 1e3).toFixed(1)}K ${currency}`
              return `${label}: ${Math.round(value)} ${currency}`
            },
          },
        },
        scales: {
          xAxes: [{ ticks: { maxRotation: 45, minRotation: 0, fontSize: 11 } }],
          yAxes: [{ ticks: { callback: (v) => (v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : v) } }],
        },
      }
    },

    sortedGroups() {
      return this.groups.slice().sort((a, b) => {
        const budgetA = this.groupBudgetRaw(a) || 0
        const budgetB = this.groupBudgetRaw(b) || 0
        return budgetB - budgetA
      })
    },

    summary() {
      let totalBudget = 0
      let totalEarned = 0
      let totalIncurred = 0
      let weightedSum = 0
      let groupsWithBudget = 0

      this.groups.forEach((group) => {
        const incurred = this.groupIncurredCostRaw(group)
        if (incurred !== null) {
          totalIncurred += incurred
        }

        const budget = this.groupBudgetRaw(group)
        if (budget === null) {
          return
        }
        groupsWithBudget += 1
        totalBudget += budget
        const progress = this.groupProgressCurrent(group)
        weightedSum += progress * budget
        totalEarned += (progress / 100) * budget
      })

      return {
        totalBudget,
        totalEarned,
        totalIncurred,
        weightedProgress: totalBudget > 0 ? weightedSum / totalBudget : 0,
        groupsWithBudget,
      }
    },

    earnedRatioLabel() {
      if (!this.summary.totalBudget) {
        return '—'
      }
      const ratio = (this.summary.totalEarned / this.summary.totalBudget) * 100
      return `${Math.round(ratio)}% ${this.$t('of_budget')}`
    },

    incurredRatioLabel() {
      if (!this.summary.totalBudget) {
        return '—'
      }
      const ratio = (this.summary.totalIncurred / this.summary.totalBudget) * 100
      return `${Math.round(ratio)}% ${this.$t('of_budget')}`
    },

    chartRows() {
      const withBudget = this.sortedGroups.filter((g) => this.groupBudgetRaw(g) !== null)
      if (!withBudget.length) {
        return []
      }

      const budgetData = []
      const earnedData = []
      const incurredData = []

      withBudget.forEach((group) => {
        const label = this.truncateLabel(group.name || group.code || String(group.id))
        const budget = this.groupBudgetRaw(group)
        const earned = Math.round(((this.groupProgressCurrent(group) / 100) * budget) * 100) / 100
        const incurred = this.groupIncurredCostRaw(group)
        budgetData.push([label, budget])
        earnedData.push([label, earned])
        incurredData.push([label, incurred === null ? 0 : incurred])
      })

      return [
        { name: this.$t('budget'), data: budgetData },
        { name: this.$t('earned_value'), data: earnedData },
        { name: this.$t('incurred_cost'), data: incurredData },
      ]
    },

    isBusy() {
      return this.isInitialLoading || this.isRefreshing
    },
  },

  watch: {
    projectId: {
      immediate: true,
      handler(projectId) {
        if (projectId) {
          this.resetAndLoad()
        }
      },
    },
  },

  methods: {
    async resetAndLoad() {
      this.breadcrumb = [{ id: null, name: this.$t('root_groups') }]
      await this.loadGroups(null)
    },

    async loadGroups(parentId) {
      if (!this.projectId) {
        this.groups = []
        return
      }

      const keepContent = this.groups.length > 0
      if (keepContent) {
        this.isRefreshing = true
      } else {
        this.isInitialLoading = true
      }

      const params = { p: this.projectId, with_groups: 1 }

      if (parentId !== null && parentId !== undefined) {
        params.parent_id = parentId
      }

      try {
        const { data } = await axios.get(`/api/v2/project/${this.projectId}/activity/groups`, { params })
        this.groups = (data && data.groups) ? data.groups : []
      } catch (error) {
        this.groups = []
        this.$notify.error('error_loading_activities')
      } finally {
        this.isInitialLoading = false
        this.isRefreshing = false
      }
    },

    canDrillDown(group) {
      return Number(group.children_count || 0) > 0
    },

    async drillDown(group) {
      if (!this.canDrillDown(group) || this.isBusy) {
        return
      }
      this.breadcrumb.push({ id: group.id, name: group.name })
      await this.loadGroups(group.id)
    },

    async navigateTo(index) {
      if (index >= this.breadcrumb.length - 1 || this.isBusy) {
        return
      }
      const crumb = this.breadcrumb[index]
      this.breadcrumb = this.breadcrumb.slice(0, index + 1)
      await this.loadGroups(crumb.id)
    },

    groupColor(group) {
      if (group.color) {
        return group.color
      }
      if (group.configs) {
        try {
          const configs = typeof group.configs === 'string' ? JSON.parse(group.configs) : group.configs
          return (configs && configs.color) ? configs.color : DEFAULT_GROUP_COLOR
        } catch (error) {
          return DEFAULT_GROUP_COLOR
        }
      }
      return DEFAULT_GROUP_COLOR
    },

    cardAccentStyle(group) {
      const color = this.groupColor(group)
      return {
        '--card-accent': color,
        borderLeftColor: color,
      }
    },

    groupProgressCurrent(group) {
      const progress = group.group_progress || {}
      const value = Number(progress.progress_current_group)
      return Number.isFinite(value) ? value : Number(group.progress) || 0
    },

    groupProgressDisplay(group) {
      return `${Math.round(this.groupProgressCurrent(group))}%`
    },

    groupBudgetRaw(group) {
      if (group.budget === null || group.budget === undefined || group.budget === '') {
        return null
      }
      const value = Number(group.budget)
      return Number.isFinite(value) ? value : null
    },

    groupBudgetDisplay(group) {
      const budget = this.groupBudgetRaw(group)
      return budget === null ? '—' : this.formatCurrencyAbbrev(budget)
    },

    groupEarnedValueDisplay(group) {
      const budget = this.groupBudgetRaw(group)
      if (budget === null) {
        return '—'
      }
      return this.formatCurrencyAbbrev((this.groupProgressCurrent(group) / 100) * budget)
    },

    groupIncurredCostRaw(group) {
      if (group.incurred_costs === null || group.incurred_costs === undefined || group.incurred_costs === '') {
        return null
      }
      const value = Number(group.incurred_costs)
      return Number.isFinite(value) ? value : null
    },

    groupIncurredCostDisplay(group) {
      const incurred = this.groupIncurredCostRaw(group)
      return incurred === null ? '—' : this.formatCurrencyAbbrev(incurred)
    },

    progressRingStyle(percent, color = '#6366f1') {
      const pct = Math.min(100, Math.max(0, Number(percent) || 0))
      return {
        background: `conic-gradient(${color} ${pct * 3.6}deg, #e2e8f0 0deg)`,
      }
    },

    formatCurrencyAbbrev(value) {
      const currency = this.projectCurrency
      const num = Number(value) || 0
      const abs = Math.abs(num)
      if (abs >= 1e6) {
        return `${(num / 1e6).toFixed(2)}M ${currency}`
      }
      if (abs >= 1e3) {
        return `${(num / 1e3).toFixed(1)}K ${currency}`
      }
      return `${Math.round(num)} ${currency}`
    },

    truncateLabel(label) {
      const text = String(label || '')
      if (text.length <= CHART_LABEL_MAX) {
        return text
      }
      return `${text.slice(0, CHART_LABEL_MAX - 1)}…`
    },
  },
}
</script>

<style scoped>
.dashboard-subpanel-budget {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.dashboard-subpanel-budget__hero {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #1e3a8a 0%, #4338ca 55%, #6366f1 100%);
  color: #fff;
}

.dashboard-subpanel-budget__hero-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.dashboard-subpanel-budget__hero-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 0.875rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
}

.dashboard-subpanel-budget__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.dashboard-subpanel-budget__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  opacity: 0.85;
}

.dashboard-subpanel-budget__breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
}

.dashboard-subpanel-budget__breadcrumb-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.dashboard-subpanel-budget__breadcrumb-item:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.22);
}

.dashboard-subpanel-budget__breadcrumb-item--active {
  background: rgba(255, 255, 255, 0.28);
  font-weight: 700;
  cursor: default;
}

.dashboard-subpanel-budget__breadcrumb-sep {
  opacity: 0.7;
}

.dashboard-subpanel-budget__loading {
  display: flex;
  justify-content: center;
  padding: 3rem 1rem;
}

.dashboard-subpanel-budget__body {
  position: relative;
}

.dashboard-subpanel-budget__body--refreshing .dashboard-subpanel-budget__kpis,
.dashboard-subpanel-budget__body--refreshing .dashboard-subpanel-budget__chart-wrap,
.dashboard-subpanel-budget__body--refreshing .dashboard-subpanel-budget__section-title,
.dashboard-subpanel-budget__body--refreshing .dashboard-subpanel-budget__grid,
.dashboard-subpanel-budget__body--refreshing .dashboard-subpanel-budget__empty {
  opacity: 0.55;
  transition: opacity 0.15s ease;
}

.dashboard-subpanel-budget__overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(1px);
  pointer-events: all;
}

.dashboard-subpanel-budget__kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  padding: 1.25rem 1.5rem 0;
}

@media (max-width: 1200px) {
  .dashboard-subpanel-budget__kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .dashboard-subpanel-budget__kpis {
    grid-template-columns: 1fr;
  }
}

.dashboard-subpanel-budget__kpi {
  position: relative;
  padding: 1rem 1.125rem;
  border-radius: 0.875rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.dashboard-subpanel-budget__kpi::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
}

.dashboard-subpanel-budget__kpi--budget::before { background: #6366f1; }
.dashboard-subpanel-budget__kpi--progress::before { background: #0ea5e9; }
.dashboard-subpanel-budget__kpi--earned::before { background: #10b981; }
.dashboard-subpanel-budget__kpi--incurred::before { background: #f59e0b; }

.dashboard-subpanel-budget__kpi-label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.dashboard-subpanel-budget__kpi-value {
  margin-top: 0.375rem;
  font-size: 1.625rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.1;
}

.dashboard-subpanel-budget__kpi-hint {
  margin-top: 0.375rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.dashboard-subpanel-budget__kpi-ring {
  position: absolute;
  top: 0.875rem;
  right: 0.875rem;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dashboard-subpanel-budget__kpi-ring span {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5625rem;
  font-weight: 800;
  color: #0f172a;
}

.dashboard-subpanel-budget__section-title {
  padding: 1.25rem 1.5rem 0.5rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #1e40af;
}

.dashboard-subpanel-budget__section-title--cards {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
}

.dashboard-subpanel-budget__section-hint {
  font-size: 0.75rem;
  font-weight: 500;
  color: #94a3b8;
}

.dashboard-subpanel-budget__chart-wrap {
  padding: 0 1rem 0.5rem;
}

.dashboard-subpanel-budget__empty {
  padding: 2rem 1.5rem;
  text-align: center;
  color: #94a3b8;
}

.dashboard-subpanel-budget__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  padding: 0.5rem 1.5rem 1.5rem;
}

.dashboard-subpanel-budget__card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-left-width: 4px;
  border-radius: 0.875rem;
  background: #fff;
  text-align: left;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.dashboard-subpanel-budget__card--clickable {
  cursor: pointer;
}

.dashboard-subpanel-budget__card--clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.15);
}

.dashboard-subpanel-budget__card--no-budget {
  opacity: 0.72;
}

.dashboard-subpanel-budget__card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.dashboard-subpanel-budget__ring {
  flex-shrink: 0;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dashboard-subpanel-budget__ring span {
  width: 2.375rem;
  height: 2.375rem;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 800;
  color: #0f172a;
}

.dashboard-subpanel-budget__card-titles {
  flex: 1;
  min-width: 0;
}

.dashboard-subpanel-budget__card-code {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--card-accent, #6366f1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dashboard-subpanel-budget__card-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.dashboard-subpanel-budget__card-chevron {
  flex-shrink: 0;
  color: #94a3b8;
}

.dashboard-subpanel-budget__card-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}

.dashboard-subpanel-budget__metric {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.5rem;
  background: #f8fafc;
}

.dashboard-subpanel-budget__metric-label {
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}

.dashboard-subpanel-budget__metric-value {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #334155;
}

.dashboard-subpanel-budget__metric-value--earned {
  color: #059669;
}

.dashboard-subpanel-budget__metric-value--incurred {
  color: #d97706;
}

.dashboard-subpanel-budget__card-bar {
  height: 4px;
  border-radius: 9999px;
  background: #e2e8f0;
  overflow: hidden;
}

.dashboard-subpanel-budget__card-bar-fill {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.4s ease;
}
</style>
